import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Check } from "lucide-react";
import Seo from "@/components/site/Seo";
import JsonLd from "@/components/site/JsonLd";
import BookCall from "@/components/site/BookCall";

/**
 * Reusable detail-page layout for all 6 service pages.
 * Props:
 *  - eyebrow (string)
 *  - title (string)
 *  - subtitle (string)
 *  - sections: [{ heading, items: string[] }]
 *  - cta (string)
 *  - seo: { title, description, path }
 *  - related: [{ to, label }]
 */
export default function ServiceDetailLayout({ eyebrow, title, subtitle, sections, cta, seo, related = [] }) {
  const absUrl = `https://xadentra.com${seo?.path || ""}`;
  return (
    <>
      <Seo {...seo} />
      <JsonLd
        id={`svc-service-${seo?.path || "x"}`}
        data={{
          "@context": "https://schema.org",
          "@type": "Service",
          name: title,
          serviceType: eyebrow,
          description: seo?.description || subtitle,
          provider: {
            "@type": "Organization",
            name: "Xadentra",
            url: "https://xadentra.com/",
          },
          areaServed: "Worldwide",
          url: absUrl,
        }}
      />
      <JsonLd
        id={`svc-breadcrumb-${seo?.path || "x"}`}
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://xadentra.com/" },
            { "@type": "ListItem", position: 2, name: "Services", item: "https://xadentra.com/services" },
            { "@type": "ListItem", position: 3, name: eyebrow, item: absUrl },
          ],
        }}
      />

      <section className="bg-[#07111F] text-white relative overflow-hidden">
        <div className="xa-blob" style={{ width: 460, height: 460, background: "#6D4DFF", top: -160, left: -120, opacity: 0.5 }} />
        <div className="xa-blob" style={{ width: 380, height: 380, background: "#00D4FF", bottom: -120, right: -100, opacity: 0.32 }} />
        <div className="absolute inset-0 xa-grid-dark opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32 relative">
          <Link to="/services" className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#00D4FF] hover:text-white inline-flex items-center gap-2" data-testid="service-back">
            ← All services
          </Link>
          <h1 className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.98] max-w-5xl">
            {title}
          </h1>
          <p className="mt-8 text-lg text-white/70 max-w-3xl leading-relaxed">{subtitle}</p>
          <div className="mt-10">
            <BookCall source="service_hero_cta">
              {cta}
            </BookCall>
          </div>
        </div>
      </section>

      <section className="bg-[#F7F9FC] border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 space-y-14">
          {sections.map((sec, i) => (
            <div key={i} data-testid={`service-section-${i}`} className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">Scope · {String(i + 1).padStart(2, "0")}</div>
                <h2 className="font-display mt-4 text-4xl lg:text-5xl font-bold tracking-tight text-[#07111F]">
                  {sec.heading}
                </h2>
              </div>
              <ul className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-3">
                {sec.items.map((it, j) => (
                  <li key={j} className="flex items-start gap-3 bg-white border border-[#E5E7EB] rounded-xl px-5 py-4">
                    <span className="mt-1 w-5 h-5 rounded-md bg-gradient-to-br from-[#6D4DFF] to-[#00D4FF] text-white grid place-items-center shrink-0">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    <span className="text-sm text-[#07111F] leading-relaxed">{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-[#07111F] text-white relative overflow-hidden">
        <div className="xa-blob pointer-events-none" style={{ width: 460, height: 460, background: "#6D4DFF", top: -160, left: -120, opacity: 0.4 }} />
        <div className="xa-blob pointer-events-none" style={{ width: 380, height: 380, background: "#00D4FF", bottom: -140, right: -100, opacity: 0.25 }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end relative">
          <div className="lg:col-span-8">
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">
              Get expert support.
            </h2>
            <p className="mt-5 text-white/70 max-w-2xl text-base lg:text-lg leading-relaxed">
              Speak directly with a senior marketing operations specialist about your setup,
              campaign operations, and next best fixes.
            </p>
          </div>
          <BookCall source="service_detail_cta" className="lg:col-span-4 lg:justify-self-end w-fit">
            {cta}
          </BookCall>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-[#0B1A2E] text-white border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#00D4FF]">Related practices</div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link key={r.to} to={r.to} className="bg-white/[0.04] border border-white/10 rounded-xl p-6 hover:border-[#6D4DFF]/50 transition-colors group">
                  <div className="font-display text-lg font-semibold text-white">{r.label}</div>
                  <div className="mt-3 inline-flex items-center gap-2 text-sm text-[#00D4FF]">
                    Explore <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
